import { QueryClient, QueryKey } from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data becomes stale after 5 minutes
      gcTime: 1000 * 60 * 30, // Cache persists for 30 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: (failureCount: number, error: unknown) => {
        // Axios puts the code on error.response.status; our fetch-based content
        // client puts it on error.status. Check both, or a 404 gets retried
        // three times with backoff before the page can say "not found".
        const err = error as AxiosError & { status?: number };
        const status = err.response?.status ?? err.status;
        if (status === 404 || status === 410 || status === 401) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Prefetch critical data
export const prefetchCriticalData = async () => {
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: ['news'],
      queryFn: () => fetch('/api/news').then(res => res.json())
    }),
    queryClient.prefetchQuery({
      queryKey: ['services'],
      queryFn: () => fetch('/api/services').then(res => res.json())
    }),
  ]);
};

// Optimistic updates helper
export const optimisticUpdate = <T>({
  queryKey,
  updateFn,
  rollbackFn,
}: {
  queryKey: QueryKey;
  updateFn: (oldData: T) => T;
  rollbackFn?: (oldData: T) => void;
}) => {
  const previousData = queryClient.getQueryData<T>(queryKey);
  
  if (previousData) {
    queryClient.setQueryData(queryKey, updateFn(previousData));
    
    return {
      rollback: () => {
        if (rollbackFn) {
          rollbackFn(previousData);
        }
        queryClient.setQueryData(queryKey, previousData);
      },
    };
  }
  
  return { rollback: () => {} };
};

// Background data synchronization
export const setupBackgroundSync = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      // Type assertion for background sync support
      const syncManager = (registration as any).sync;
      if (syncManager) {
        syncManager.register('sync-data').catch((error: Error) => {
          console.error('Background sync registration failed:', error);
        });
      }
    });
  }
};

// Parallel queries helper
export const parallelQueries = async <T>({
  queries,
}: {
  queries: Array<{
    queryKey: QueryKey;
    queryFn: () => Promise<T>;
  }>;
}) => {
  return Promise.all(
    queries.map(({ queryKey, queryFn }) =>
      queryClient.fetchQuery({
        queryKey,
        queryFn
      })
    )
  );
}; 